import { DeleteOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Modal } from 'antd'
import { toast } from 'sonner'

import { brandsQueries } from '@/api/brands/brandsQueries'
import Loader from '@/components/Atoms/Loader/Loader'
import { useCreateBrandAliasMutation } from '@/hooks/brands/useCreateBrandAliasMutation'
import { useDeleteBrandAliasMutation } from '@/hooks/brands/useDeleteBrandAliasMutation'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import AddBrandAliasForm from './AddBrandAliasForm/AddBrandAliasForm'
import { BRAND_ALIASES_DESCRIPTION } from './BrandAliasesModal.config'
import cn from './BrandAliasesModal.module.scss'

interface BrandAliasesModalProps {
  brand: { id: string; description: string }
  canEdit: boolean
  onClose: () => void
}

function BrandAliasesModal({
  brand,
  canEdit,
  onClose,
}: BrandAliasesModalProps) {
  const [modal, contextHolder] = Modal.useModal()
  const aliasesQuery = useQuery(brandsQueries.aliases(brand.id))
  const createAlias = useCreateBrandAliasMutation(brand.id)
  const deleteAlias = useDeleteBrandAliasMutation(brand.id)

  const handleAdd = (alias: string, onAdded: () => void) => {
    createAlias.mutate(alias, {
      onSuccess: () => {
        toast.success('Синоним добавлен')
        onAdded()
      },
      onError: (error) => handleApiError(error, modal),
    })
  }

  const handleDelete = (aliasId: string) => {
    if (deleteAlias.isPending) return
    deleteAlias.mutate(aliasId, {
      onSuccess: () => toast.success('Синоним удалён'),
      onError: (error) => handleApiError(error, modal),
    })
  }

  const renderAliases = () => {
    if (aliasesQuery.isPending) return <Loader size={24} />
    if (aliasesQuery.isError) {
      return (
        <div className={cn.state}>
          {getGeneralErrorMessage(aliasesQuery.error)}{' '}
          <Button type="link" onClick={() => aliasesQuery.refetch()}>
            Повторить
          </Button>
        </div>
      )
    }
    if (aliasesQuery.data.length === 0) {
      return <div className={cn.state}>Синонимов пока нет</div>
    }
    return (
      <ul className={cn.list}>
        {aliasesQuery.data.map((alias) => (
          <li key={alias.id} className={cn.item}>
            <span>{alias.alias}</span>
            {canEdit && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Удалить синоним ${alias.alias}`}
                loading={
                  deleteAlias.isPending && deleteAlias.variables === alias.id
                }
                onClick={() => handleDelete(alias.id)}
              />
            )}
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Modal
      open
      centered
      destroyOnHidden
      title={`Синонимы бренда «${brand.description}»`}
      footer={null}
      onCancel={onClose}
    >
      {contextHolder}
      <p className={cn.description}>{BRAND_ALIASES_DESCRIPTION}</p>
      {renderAliases()}
      {canEdit && (
        <AddBrandAliasForm
          isPending={createAlias.isPending}
          onAdd={handleAdd}
        />
      )}
    </Modal>
  )
}

export default BrandAliasesModal
